/**
 * ProfileSettings page - Manage user profile and password
 */

import { ArrowLeft, Save, Loader2, Lock, User, Globe } from 'lucide-react';
import { useState, useEffect } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { useProfile, useUpdateProfile } from '@/hooks/useProfile';
import { cn } from '@/lib/utils';
import { changePassword } from '@/services/auth';

// ============================================================================
// TYPES
// ============================================================================

type TabType = 'profile' | 'security';

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'UTC', label: 'UTC (Coordinated Universal Time)' },
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEST/AEDT)' },
];

// ============================================================================
// TAB NAVIGATION COMPONENT
// ============================================================================

interface TabButtonProps {
  label: string;
  value: TabType;
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}

function TabButton({ label, active, onClick, icon }: TabButtonProps) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'px-4 py-2 text-sm font-semibold rounded-lg transition-all inline-flex items-center gap-2',
        active
          ? 'bg-brand/10 text-brand'
          : 'text-secondary hover:bg-surface-subtle hover:text-primary'
      )}
      aria-current={active ? 'page' : undefined}
    >
      {icon}
      {label}
    </button>
  );
}

// ============================================================================
// PASSWORD CHANGE FORM
// ============================================================================

function PasswordChangeForm() {
  const toast = useToast();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChanging, setIsChanging] = useState(false);
  const [errors, setErrors] = useState<{
    currentPassword?: string;
    newPassword?: string;
    confirmPassword?: string;
  }>({});

  const validateForm = (): boolean => {
    const newErrors: typeof errors = {};

    if (!currentPassword) {
      newErrors.currentPassword = 'Current password is required';
    }

    if (!newPassword) {
      newErrors.newPassword = 'New password is required';
    } else if (newPassword.length < 8) {
      newErrors.newPassword = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (newPassword !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    setIsChanging(true);
    try {
      await changePassword({
        currentPassword,
        newPassword,
      });
      toast.success('Password changed', 'Your password has been updated successfully');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to change password';
      if (message.includes('Incorrect')) {
        setErrors({ currentPassword: 'Current password is incorrect' });
      } else {
        toast.error('Failed to change password', message);
      }
    } finally {
      setIsChanging(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <Input
        type="password"
        label="Current Password"
        value={currentPassword}
        onChange={(e) => {
          setCurrentPassword(e.target.value);
          if (errors.currentPassword) setErrors((prev) => ({ ...prev, currentPassword: undefined }));
        }}
        placeholder="Enter your current password"
        error={!!errors.currentPassword}
        errorMessage={errors.currentPassword}
        disabled={isChanging}
        required
      />

      <Input
        type="password"
        label="New Password"
        value={newPassword}
        onChange={(e) => {
          setNewPassword(e.target.value);
          if (errors.newPassword) setErrors((prev) => ({ ...prev, newPassword: undefined }));
        }}
        placeholder="Enter your new password"
        error={!!errors.newPassword}
        errorMessage={errors.newPassword}
        disabled={isChanging}
        required
      />

      <Input
        type="password"
        label="Confirm New Password"
        value={confirmPassword}
        onChange={(e) => {
          setConfirmPassword(e.target.value);
          if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: undefined }));
        }}
        placeholder="Confirm your new password"
        error={!!errors.confirmPassword}
        errorMessage={errors.confirmPassword}
        disabled={isChanging}
        required
      />

      <div className="flex items-center justify-end pt-4 border-t border-border-subtle">
        <Button
          type="submit"
          leftIcon={<Lock className="h-4 w-4" />}
          loading={isChanging}
          disabled={!currentPassword || !newPassword || !confirmPassword}
        >
          Change Password
        </Button>
      </div>
    </form>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function ProfileSettings() {
  const navigate = useNavigate();
  const toast = useToast();

  const { data: profile, isLoading, error } = useProfile();
  const updateProfileMutation = useUpdateProfile();

  const [activeTab, setActiveTab] = useState<TabType>('profile');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [timezone, setTimezone] = useState('UTC');
  const [firstNameError, setFirstNameError] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (profile) {
      setFirstName(profile.firstName || '');
      setLastName(profile.lastName || '');
      setTimezone(profile.timezone || 'UTC');
    }
  }, [profile]);

  const validateName = (value: string, fieldName: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed) return `${fieldName} is required`;
    if (trimmed.length > 100) return `${fieldName} cannot exceed 100 characters`;
    return null;
  };

  const handleFirstNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setFirstName(e.target.value);
    setHasChanges(true);
    if (firstNameError) setFirstNameError('');
  };

  const handleLastNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    setLastName(e.target.value);
    setHasChanges(true);
    if (lastNameError) setLastNameError('');
  };

  const handleTimezoneChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setTimezone(e.target.value);
    setHasChanges(true);
  };

  const handleSaveProfile = async () => {
    const trimmedFirstName = firstName.trim();
    const trimmedLastName = lastName.trim();

    // Validate names - cannot be cleared once set
    const firstNameErr = profile?.firstName ? validateName(trimmedFirstName, 'First name') : null;
    const lastNameErr = profile?.lastName ? validateName(trimmedLastName, 'Last name') : null;

    if (firstNameErr) {
      setFirstNameError(firstNameErr);
      return;
    }
    if (lastNameErr) {
      setLastNameError(lastNameErr);
      return;
    }

    try {
      await updateProfileMutation.mutateAsync({
        firstName: trimmedFirstName || undefined,
        lastName: trimmedLastName || undefined,
        timezone,
      });
      setHasChanges(false);
      toast.success('Profile updated', 'Your profile has been saved');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast.error('Failed to update profile', message);
    }
  };

  const handleBack = () => {
    navigate('/projects');
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 text-brand animate-spin" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card>
          <div className="text-center py-12">
            <h3 className="text-lg font-semibold text-danger mb-2">
              Failed to load profile
            </h3>
            <p className="text-sm text-secondary mb-6">
              {error?.message || 'Profile not found'}
            </p>
            <Button onClick={() => navigate('/projects')} variant="secondary">
              Back to Projects
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <button
          onClick={handleBack}
          className="inline-flex items-center gap-2 text-sm font-medium text-secondary hover:text-primary transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Projects
        </button>

        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="text-3xl font-black text-primary leading-tight tracking-tight">
              Settings
            </h1>
            <p className="text-sm text-secondary mt-2">
              Manage your profile and security settings
            </p>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-2 border-b border-border-subtle pb-1">
        <TabButton
          label="Profile"
          value="profile"
          active={activeTab === 'profile'}
          onClick={() => setActiveTab('profile')}
          icon={<User className="h-4 w-4" />}
        />
        <TabButton
          label="Security"
          value="security"
          active={activeTab === 'security'}
          onClick={() => setActiveTab('security')}
          icon={<Lock className="h-4 w-4" />}
        />
      </div>

      {/* Tab Content */}
      {activeTab === 'profile' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                Profile Information
              </h2>
              <p className="text-sm text-secondary">
                Update your personal information
              </p>
            </div>

            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-primary mb-1.5">
                Email
              </label>
              <div className="px-3 py-2 bg-surface-subtle rounded-lg text-sm text-secondary">
                {profile.email}
              </div>
              <p className="text-xs text-muted mt-1">
                Email cannot be changed as it is your account identifier
              </p>
            </div>

            {/* First Name */}
            <Input
              label="First Name"
              value={firstName}
              onChange={handleFirstNameChange}
              placeholder="Enter your first name"
              error={!!firstNameError}
              errorMessage={firstNameError}
              disabled={updateProfileMutation.isPending}
              required={!!profile?.firstName}
            />

            {/* Last Name */}
            <Input
              label="Last Name"
              value={lastName}
              onChange={handleLastNameChange}
              placeholder="Enter your last name"
              error={!!lastNameError}
              errorMessage={lastNameError}
              disabled={updateProfileMutation.isPending}
              required={!!profile?.lastName}
            />

            {/* Timezone */}
            <div>
              <label
                htmlFor="timezone"
                className="block text-sm font-medium text-primary mb-1.5"
              >
                <span className="inline-flex items-center gap-1.5">
                  <Globe className="h-4 w-4 text-muted" />
                  Timezone
                </span>
              </label>
              <select
                id="timezone"
                value={timezone}
                onChange={handleTimezoneChange}
                disabled={updateProfileMutation.isPending}
                className={cn(
                  'w-full px-3 py-2 rounded-lg border bg-surface text-sm',
                  'border-border-default focus:border-brand focus:ring-1 focus:ring-brand',
                  'disabled:bg-surface-subtle disabled:cursor-not-allowed'
                )}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.value} value={tz.value}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border-subtle">
              <Button
                onClick={handleSaveProfile}
                leftIcon={<Save className="h-4 w-4" />}
                loading={updateProfileMutation.isPending}
                disabled={!hasChanges || updateProfileMutation.isPending}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Card>
      )}

      {activeTab === 'security' && (
        <Card>
          <div className="space-y-6">
            <div>
              <h2 className="text-lg font-semibold text-primary mb-1">
                Change Password
              </h2>
              <p className="text-sm text-secondary">
                Update your account password
              </p>
            </div>

            <PasswordChangeForm />
          </div>
        </Card>
      )}
    </div>
  );
}
